import React, { useState, useEffect, useMemo } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Edit, Save } from "lucide-react";
import { ClientApi } from "@/api/client.api";
import { toast } from "sonner";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "@/constants/array/country_city.json";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LoaderButton } from "@/components/ui/loader-button";
import {
  clientContactNumberSchema,
  sanitizeClientContactNumber,
} from "@/utils/client-contact.utils";

const CURRENCY_OPTIONS = [
  { id: 1, label: "United State Dollar", value: "USD" },
  { id: 2, label: "EURO", value: "EUR" },
  { id: 3, label: "British Pound", value: "GBP" },
  { id: 4, label: "Indian Rupee", value: "INR" },
  { id: 5, label: "United Arab Emirates Dirham", value: "AED" },
  { id: 6, label: "Ethiopian Birr", value: "ETB" },
];

const ClientUpdateSchema = Yup.object().shape({
  clientName: Yup.string().required("Client Name is required"),
  clientContactNumber: clientContactNumberSchema,
  clientEmail: Yup.string()
    .email("Invalid email address")
    .required("Client Email is required"),
  clientCountry: Yup.string().required("Country is required"),
  currency: Yup.string().required("Currency is required"),
  clientAddress: Yup.string().required("Address is required"),
});

const ClientDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = location.state?.client;

  const mappedClient = useMemo(
    () =>
      client && {
        id: client.id,
        clientName: client.name,
        clientContactNumber: client.contact,
        clientEmail: client.email,
        clientCode: client.code,
        clientAddress: client.address,
        clientCountry: client.country,
        currency: client.currency,
        createDate: client.createdAt,
        businessAnalyticsLink: client.analytics,
      },
    [client]
  );
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.tab || "details");
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm({
    resolver: yupResolver(ClientUpdateSchema),
    defaultValues: {
      clientName: mappedClient?.clientName || "",
      clientContactNumber: sanitizeClientContactNumber(
        mappedClient?.clientContactNumber || ""
      ),
      clientEmail: mappedClient?.clientEmail || "",
      clientCountry:
        (countries as any[]).find(
          (c) =>
            c.name.toLowerCase() ===
            (mappedClient?.clientCountry || "").toLowerCase()
        )?.name || "",
      currency: mappedClient?.currency || "USD",
      clientAddress: mappedClient?.clientAddress || "",
    },
  });

  useEffect(() => {
    if (mappedClient) {
      form.reset({
        clientName: mappedClient.clientName || "",
        clientContactNumber: sanitizeClientContactNumber(
          mappedClient.clientContactNumber || ""
        ),
        clientEmail: mappedClient.clientEmail || "",
        clientCountry:
          (countries as any[]).find(
            (c) =>
              c.name.toLowerCase() ===
              (mappedClient.clientCountry || "").toLowerCase()
          )?.name || "",
        currency: mappedClient.currency || "USD",
        clientAddress: mappedClient.clientAddress || "",
      });
    }
  }, [mappedClient, form]);

  // Reset form values when opening the sheet
  const handleEditOpen = () => {
    form.reset({
      clientName: mappedClient?.clientName || "",
      clientContactNumber: sanitizeClientContactNumber(
        mappedClient?.clientContactNumber || ""
      ),
      clientEmail: mappedClient?.clientEmail || "",
      clientCountry:
        (countries as any[]).find(
          (c) =>
            c.name.toLowerCase() ===
            (mappedClient?.clientCountry || "").toLowerCase()
        )?.name || "",
      currency: mappedClient?.currency || "USD",
      clientAddress: mappedClient?.clientAddress || "",
    });
    setIsEditSheetOpen(true);
  };

  const getClientListPath = () => {
    if (location.pathname.startsWith("/xpandpoint"))
      return "/xpandpoint/clients";
    if (location.pathname.startsWith("/rewardpoint"))
      return "/rewardpoint/clients";
    if (location.pathname.startsWith("/accesspoint"))
      return "/accesspoint/clients";
    return "/clients";
  };

  if (!mappedClient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to={getClientListPath()}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
        </div>
        <Card className="w-full border-0 shadow-sm card-modern">
          <CardContent className="p-6">
            <p className="text-center text-gray-500">
              No client data found. Please access this page from the Clients
              list.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSave = async (formData: any) => {
    try {
      setIsSaving(true);
      const currencyId =
        CURRENCY_OPTIONS.find(
          (currency) => currency.value === formData.currency
        )?.id || 1;

      const payload = {
        clientName: formData.clientName,
        clientAddress: formData.clientAddress,
        clientContactNumber: formData.clientContactNumber,
        clientEmail: formData.clientEmail,
        clientCountry: formData.clientCountry,
        currencyId: currencyId,
      };

      await ClientApi.updateClient(mappedClient.id, payload);
      toast.success("Client updated successfully");
      setIsEditSheetOpen(false);
      navigate(getClientListPath());
    } catch (error) {
      toast.error(error?.message || "Failed to update client");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await ClientApi.deleteClient(mappedClient.id);
      toast.success("Client deleted successfully");
      navigate(getClientListPath());
    } catch (error) {
      toast.error(error?.message || "Failed to delete client");
    }
  };

  const METABASE_DASHBOARD_URL = import.meta.env.VITE_METABASE_DASHBOARD_URL;
  console.log(
    `https://metabase.dev.loyyal.net/public/dashboard/35abcebc-e7d9-470b-a899-6ded9af11d27?client_id=${mappedClient.id}&date=&time_grouping=`,
    "hgjghgg----"
  );

  return (
    <div className="space-y-6">
      {/* Header with back button and edit */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to={getClientListPath()}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {mappedClient.clientName}
            </h1>
            <p className="text-gray-500">Client Details</p>
          </div>
        </div>
        <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
          <SheetTrigger asChild>
            <Button
              onClick={handleEditOpen}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit Client
            </Button>
          </SheetTrigger>
          <SheetContent className="sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Edit Client</SheetTitle>
              <SheetDescription>
                Make changes to your client here. Click save when you're done.
              </SheetDescription>
            </SheetHeader>
            <div className="py-4">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSave)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="clientName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Name*</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="clientContactNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Number*</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. 501234567"
                            inputMode="numeric"
                            maxLength={15}
                            {...field}
                            onChange={(e) =>
                              field.onChange(
                                sanitizeClientContactNumber(e.target.value)
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="clientEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Email*</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. client@email.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="clientCountry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(countries as any)?.map((country) => (
                              <SelectItem
                                key={country?.name}
                                value={country?.name}
                              >
                                {country?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency*</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CURRENCY_OPTIONS.map((currency) => (
                              <SelectItem
                                key={currency.value}
                                value={currency.value}
                              >
                                {currency.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="clientAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address*</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end">
                    <LoaderButton
                      type="submit"
                      className="flex items-center gap-2"
                      loading={isSaving}
                    >
                      <Save className="h-4 w-4" /> Save Changes
                    </LoaderButton>
                  </div>
                </form>
              </Form>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="details">Client Details</TabsTrigger>
          <TabsTrigger value="dashboard">Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Client Code
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.clientCode}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Client Name
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.clientName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Contact Number
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.clientContactNumber}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Client Email
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.clientEmail || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Currency
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.currency}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Created Date
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.createDate
                      ? format(
                          new Date(mappedClient.createDate),
                          "MMM d, yyyy, h:mm a"
                        )
                      : "N/A"}
                  </p>
                </div>
                {mappedClient.businessAnalyticsLink && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Business Analytics Link
                    </label>
                    <a
                      href={mappedClient.businessAnalyticsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 break-all"
                    >
                      {mappedClient.businessAnalyticsLink}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Address Section */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Address
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.clientAddress}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Country
                  </label>
                  <p className="text-sm text-gray-900">
                    {mappedClient.clientCountry}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="dashboard">
          {location.pathname.startsWith("/xpandpoint") ? (
            <iframe
              // src={`${METABASE_DASHBOARD_URL}/35abcebc-e7d9-470b-a899-6ded9af11d27?client_id=1&date=&time_grouping=`}
              src={`https://metabase.dev.loyyal.net/public/dashboard/35abcebc-e7d9-470b-a899-6ded9af11d27?client_id=${mappedClient.id}&date=&time_grouping=`}
              // src={`https://metabase.dev.loyyal.io/public/dashboard/35abcebc-e7d9-470b-a899-6ded9af11d27?client_id=${mappedClient.id}&date=&time_grouping=`}
              title="Client Dashboard"
              width="100%"
              style={{
                borderRadius: 8,
                minHeight: 300,
                height: "71vh",
                maxHeight: "80vh",
              }}
              frameBorder="0"
              allowFullScreen
            />
          ) : location.pathname.startsWith("/rewardpoint") ? (
            <div className="text-center text-gray-500 py-12">
              Dashboard link coming soon.
            </div>
          ) : (
            <iframe
              src={`https://metabase.dev.loyyal.net/public/dashboard/d8c2141a-9308-4453-8ea8-a8b0e8055b3a?client_id=${mappedClient.id}&date_group=&date_range=&merchant_names=&program_names=`}
              // src={`${METABASE_DASHBOARD_URL}/d8c2141a-9308-4453-8ea8-a8b0e8055b3a?client_id=${mappedClient.id}&date_group=&date_range=&merchant_names=&program_names=`}
              title="Client Dashboard"
              width="100%"
              style={{
                borderRadius: 8,
                minHeight: 300,
                height: "71vh",
                maxHeight: "80vh",
              }}
              frameBorder="0"
              allowFullScreen
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientDetails;

<style>{`.loader { border: 2px solid #f3f3f3; border-top: 2px solid #3498db; border-radius: 50%; width: 16px; height: 16px; animation: spin 1s linear infinite; display: inline-block; vertical-align: middle; } @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>;
